import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Grid,
  Card,
  useTheme,
} from "@mui/material";
import SignalsControls from "./SignalsControls";
import SignalsDisplay from "./SignalsDisplay";
import HistoricalSignalsDisplay from "../Historical/HistoricalSignalsDisplay";
import { useSignalsState } from "../../../hooks/useOptimizedState";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { removeActiveRequests, updateLoading } from "../../../Redux/Reducers/uiSlice";
import { setHistoricalSignalSet } from "../../../Redux/Reducers/strategiesSlice";
import { selectActiveRequests } from "../../../Redux/Reducers/selectors";
import {
  isNonTradingDay,
  getNextTradingDay,
  getPreviousTradingDay,
  adjustSignalSetDates
} from "../../../utils/tradingDayUtils";
import {
  historicalSignalSetsForStrategy,
  toLegacyWeights,
  requestHistoricalSignalSet,
  cancelHistoricalSignalFetch,
} from "../../../utils/historicalSignalSets";

const SignalsContent = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [pasted, setPasted] = useState([]);
  const [strategyVal, setStrategyVal] = useState("");
  const [strategyValObj, setStrategyValObj] = useState();

  const [verifyEnabled, setVerifyEnabled] = useState(false);
  const [copiedData, setCopiedData] = useState([]);
  const [openPopup, setOpenPopup] = useState(false);
  const [handleVerify, setHandleVerify] = useState(() => () => { });
  const [setSaveChange, setSaveChangeHandler] = useState(() => () => { });
  const [openBlacklistPopup, setBlacklistOpenPopup] = useState(false);
  const [numberOfBlacklistedObjects, setNumberOfBlacklistedObjects] = useState();
  const [numberOfInvalidObjects, setNumberOfInvalidObjects] = useState();
  const [numberOfNotVerifiedObjects, setNumberOfNotVerifiedObjects] = useState();
  const [empty, setEmpty] = useState(false);
  const [pasteButton, setPasteButton] = useState(false);
  const [error, setError] = useState("");

  const timeoutRef = useRef(null);
  const activeRequestsRef = useRef([]);

  const { strategies, blacklistSignal, isStrategist, isInvestor, managers, historicalSignalSet } = useSignalsState();
  const activeRequests = useSelector(selectActiveRequests);
  const isFetchingHistoricalSignals = activeRequests?.includes("get_historical_signal_set") || false;

  useEffect(() => {
    activeRequestsRef.current = activeRequests;
  }, [activeRequests]);

  const strategySelected = (id) => {
    cancelHistoricalSignalFetch();
    if (id === null) {
      setStrategyValObj(undefined);
      dispatch(setHistoricalSignalSet([]));
    } else {
      setStrategyValObj(id);
      dispatch(setHistoricalSignalSet([]));
    }
  };

  const strategyList = strategies
    ?.map((i) => i?.name)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

  // Function to fetch signals (rebalanced or all)
  const handleFetchSignals = useCallback(async (fetchAll = false) => {
    if (!strategyValObj) {
      return;
    }

    try {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      dispatch(
        requestHistoricalSignalSet({
          strategy: strategyValObj,
          fetchAllSignalsets: fetchAll,
          fetchPerformance: false,
          fetchBacktestSummary: false,
          replace: true,
          loadingMsg: `Fetching ${fetchAll ? "all" : "rebalanced"} signals for ${strategyVal}...`,
        })
      );

      timeoutRef.current = setTimeout(() => {
        const stillFetching = activeRequestsRef.current.includes("get_historical_signal_set");
        if (stillFetching) {
          cancelHistoricalSignalFetch();
          dispatch(setHistoricalSignalSet([]));
          dispatch(removeActiveRequests("get_historical_signal_set"));
          dispatch(updateLoading(false));
          toast.error(`Unable to fetch ${fetchAll ? 'all' : 'rebalanced'} signals`);
        }
        timeoutRef.current = null;
      }, 120000);
    } catch (error) {
      console.error("Error sending websocket request:", error);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [strategyValObj, strategyVal, dispatch]);

  // Wrapper functions for backward compatibility
  const handleFetchHistoricalSignals = useCallback(() => handleFetchSignals(false), [handleFetchSignals]);
  const handleFetchAllSignals = useCallback(() => handleFetchSignals(true), [handleFetchSignals]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  // Function to fill time series with signal data
  function fillTimeSeries(signalSets) {
    if (signalSets.length === 0) return [];

    const filledSeries = [];

    for (let i = 0; i < signalSets.length; i++) {
      const currentSignal = signalSets[i];
      const currentDate = new Date(currentSignal.date);

      if (i === 0) {
        // For the first signal, add it with its own data
        filledSeries.push({
          date: currentDate.getTime(),
          data: currentSignal
        });
      } else {
        // Get the previous signal date
        const previousSignal = signalSets[i - 1];
        const previousDate = new Date(previousSignal.date);

        // Fill days between previous signal and current signal with previous signal's data
        // Use getPreviousTradingDay to find the last day before current signal
        const lastDayToFill = getPreviousTradingDay(currentDate);

        // Fill from day after previous signal up to and including lastDayToFill
        let fillDate = getNextTradingDay(previousDate);
        while (fillDate <= lastDayToFill) {
          filledSeries.push({
            date: fillDate.getTime(),
            data: previousSignal
          });
          fillDate = getNextTradingDay(fillDate);
        }

        // Add the current signal with its own data
        filledSeries.push({
          date: currentDate.getTime(),
          data: currentSignal
        });
      }
    }

    // Extend the last signal forward until the previous trading day (yesterday's trading day)
    if (signalSets.length > 0) {
      const lastSignal = signalSets[signalSets.length - 1];
      const lastDate = new Date(lastSignal.date);

      // Get today's date and find the previous trading day
      const today = new Date();
      const endDate = getPreviousTradingDay(today);

      // Fill from day after last signal up to and including endDate
      let fillDate = getNextTradingDay(lastDate);
      while (fillDate <= endDate) {
        filledSeries.push({
          date: fillDate.getTime(),
          data: lastSignal
        });
        fillDate = getNextTradingDay(fillDate);
      }
    }

    return filledSeries;
  }

  // Function to download CSV
  const downloadCSV = () => {
    const filteredData = historicalSignalSetsForStrategy(
      historicalSignalSet,
      strategyValObj
    );

    if (!strategyValObj || filteredData.length === 0) {
      toast.error("No signal data available.");
      return;
    }

    // STEP 1: Sort by date ascending order
    const sortedData = filteredData.sort((a, b) => a.date - b.date);

    // STEP 2: Use adjustSignalSetDates function
    const adjustedData = adjustSignalSetDates(sortedData);

    // STEP 3: Fill time series with all trading days
    const filledTimeSeries = fillTimeSeries(adjustedData);

    // Sort filled time series by date
    filledTimeSeries.sort((a, b) => a.date - b.date);

    // STEP 4: Generate CSV with signal weights
    // Get all unique signal names across all dates
    const allSignalNames = new Set();
    filledTimeSeries.forEach((timeSeriesItem) => {
      const signalSet = timeSeriesItem.data;
      const { weights_keys, weights_values } = toLegacyWeights(signalSet);
      Object.keys(weights_values).forEach((key) => {
        const tickerInfo = weights_keys[key];
        if (tickerInfo && tickerInfo.symbol) {
          allSignalNames.add(tickerInfo.symbol.toUpperCase());
        }
      });
    });
    // Sort signal names alphabetically
    const sortedSignalNames = Array.from(allSignalNames).sort();

    // Create CSV header with signal names and dates
    const csvHeader = [
      "",
      ...filledTimeSeries.map((timeSeriesItem) => {
        const dateObj = new Date(timeSeriesItem.date);
        return `${dateObj.getMonth() + 1
          }/${dateObj.getDate()}/${dateObj.getFullYear()}`;
      }),
    ];

    // Create CSV rows with signal names
    const csvRows = [csvHeader];

    sortedSignalNames.forEach((signalName) => {
      const row = [signalName]; // Add signal name as first column

      // Add values for each date
      filledTimeSeries.forEach((timeSeriesItem) => {
        const signalSet = timeSeriesItem.data;
        let value = "";

        // Find the signal in this date's data
        const { weights_keys, weights_values } = toLegacyWeights(signalSet);
        Object.keys(weights_values).forEach((key) => {
          const tickerInfo = weights_keys[key];
          if (
            tickerInfo &&
            tickerInfo.symbol &&
            tickerInfo.symbol.toUpperCase() === signalName
          ) {
            const weight = weights_values[key];
            value = parseFloat(weight.toFixed(4)).toString();
          }
        });

        // Replace empty values with "0"
        row.push(value || "0");
      });

      csvRows.push(row);
    });

    // Get date range for filename
    const startDate = new Date(filledTimeSeries[0].date);
    const endDate = new Date(filledTimeSeries[filledTimeSeries.length - 1].date);
    const startDateStr = `${startDate.getMonth() + 1
      }-${startDate.getDate()}-${startDate.getFullYear()}`;
    const endDateStr = `${endDate.getMonth() + 1
      }-${endDate.getDate()}-${endDate.getFullYear()}`;

    // Convert to CSV string
    const csvContent = csvRows
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    // Create and download file
    try {
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `historical_signals_${strategyVal}_${startDateStr}-${endDateStr}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("CSV file downloaded successfully.");
    } catch (error) {
      console.error("Error downloading CSV file:", error);
      toast.error("Failed to download CSV file.");
    }
  };

  const hasData =
    Boolean(strategyValObj) &&
    historicalSignalSetsForStrategy(historicalSignalSet, strategyValObj).length > 0;

  return (
    <Grid container spacing={3}>
      {/* Controls Section */}
      <Grid size={{ xs: 12 }}>
        <Card
          sx={{
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: `1px solid ${theme.palette.divider}`,
            p: 3,
            width: "100%",

          }}
        >
          <SignalsControls
            strategyList={strategyList}
            strategyVal={strategyVal}
            setStrategyVal={setStrategyVal}
            strategyValObj={strategyValObj}
            strategySelected={strategySelected}
            strategies={strategies}
            pasted={pasted}
            setPasted={setPasted}
            setVerifyEnabled={setVerifyEnabled}
            setCopiedData={setCopiedData}
            setHandleVerify={setHandleVerify}
            setSaveChangeHandler={setSaveChangeHandler}
            setNumberOfInvalidObjects={setNumberOfInvalidObjects}
            setEmpty={setEmpty}
            setError={setError}
            error={error}
            empty={empty}
            blacklist={blacklistSignal}
            isStrategist={isStrategist}
            numberOfInvalidObjects={numberOfInvalidObjects}
            setSaveChange={setSaveChange}
            onFetchHistoricalSignals={handleFetchHistoricalSignals}
            isFetchingHistoricalSignals={isFetchingHistoricalSignals}
            onFetchAllSignals={handleFetchAllSignals}
            isFetchingAllSignals={isFetchingHistoricalSignals}
            isManager={!isStrategist && !isInvestor}
            managerId={managers?.[0]?.managerId}
          />
        </Card>
      </Grid>

      {/* Display Section */}
      <Grid size={{ xs: 12 }}>
        <Card
          sx={{
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: `1px solid ${theme.palette.divider}`,
            p: 3,
            height: {lg: '100dvh'},
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <SignalsDisplay
            strategies={strategies}
            strategyValObj={strategyValObj}
            strategyVal={strategyVal}
            pasted={pasted}
            setPasted={setPasted}
            setPasteButton={setPasteButton}
            setVerifyEnabled={setVerifyEnabled}
            copiedData={copiedData}
            setCopiedData={setCopiedData}
            setHandleVerify={setHandleVerify}
            setSaveChangeHandler={setSaveChangeHandler}
            setNumberOfInvalidObjects={setNumberOfInvalidObjects}
            setEmpty={setEmpty}
            setError={setError}
            error={error}
            empty={empty}
            numberOfInvalidObjects={numberOfInvalidObjects}
            blacklist={blacklistSignal}
            isStrategist={isStrategist}
          />
        </Card>
      </Grid>

      {/* Historical Signals Display Section - Only show when data exists */}
      {hasData && (
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: `1px solid ${theme.palette.divider}`,
              p: 3,
            }}
          >
            <HistoricalSignalsDisplay
              strategies={strategies}
              strategyValObj={strategyValObj}
              strategyVal={strategyVal}
              isStrategist={isStrategist}
              historicalSignalSet={historicalSignalSet}
              onDownload={downloadCSV}
              hasData={hasData}
              isFetchingHistoricalSignals={isFetchingHistoricalSignals}
            />
          </Card>
        </Grid>
      )}

    </Grid>
  );
};

export default SignalsContent;
